import React, { ComponentType, ReactElement, ReactNode, useEffect } from 'react';
import useCurrentUser from 'hooks/useCurrentUser';
import { MedelyLogo } from '@medely/web-components/icons';
import { useTheme } from '@medely/web-components/themes';
import { Box, Heading, Text, useAnalytics } from '@medely/ui-kit';
import ProArchivedView from './ProArchivedView';
import { DeactivatedCard } from '@mp/components';

const HeadingComponent = Heading as ComponentType<{ size: string; children?: ReactNode }>;

const ProRejectedView = () => {
  const theme = useTheme();

  return (
    <>
      <MedelyLogo color={theme.palette.primary.main} />
      <Box py={3}>
        <HeadingComponent size="xs">
          Thank you for your interest in joining Medely.
        </HeadingComponent>
      </Box>
      <Box pb={3}>
        <Text size="m">
          At this time, your application has been declined because you do not meet one or more of
          Medely&apos;s requirements.
        </Text>
      </Box>
    </>
  );
};

interface IProViewProps {
  children?: ReactNode | ReactNode[];
}

const ProViewContent = ({ children }: IProViewProps): ReactElement => {
  const { isArchived, isRejected, currentUser, isDeactivated, isDeletionRequested } =
    useCurrentUser();
  const accountId = currentUser?.id;

  const analytics = useAnalytics();

  const isArchivedOrRejected = isArchived || isRejected;

  const trackName = isRejected
    ? 'Product Application - Application Rejected - Viewed'
    : 'Product Application - Application Archived - Viewed';

  const applicationVersion = isRejected ? 'rejected' : 'archived';

  useEffect(() => {
    if (isArchivedOrRejected) {
      analytics.track(trackName, {
        account_id: accountId,
        application_version: applicationVersion,
      });
    }
  }, []);

  if (isDeactivated) {
    return <DeactivatedCard isDeletionRequest={isDeletionRequested} hasDisplayLinks />;
  }

  if (isArchived) {
    return <ProArchivedView />;
  }

  if (isRejected) {
    return <ProRejectedView />;
  }

  return <>{children}</>;
};

export default ProViewContent;
